import { logger } from '../utils/logger';
/**
 * Email Service for RepConnect
 * Handles sending emails for admin invitations and notifications
 */

class EmailService {
  constructor() {
    this.apiKey = process.env.REACT_APP_EMAIL_API_KEY;
    this.fromEmail = process.env.REACT_APP_FROM_EMAIL || 'support@support.meetwithreps.com';
    this.emailProvider = process.env.REACT_APP_EMAIL_PROVIDER || 'resend'; // 'resend', 'sendgrid', 'mailgun'
  }

  /**
   * Send admin invitation email
   */
  async sendAdminInvite(adminUser, physicianName, inviteUrl) {
    const emailData = {
      to: adminUser.email,
      subject: `Admin Invitation - ${physicianName}'s RepConnect Portal`,
      html: this.generateAdminInviteHTML(adminUser, physicianName, inviteUrl),
      text: this.generateAdminInviteText(adminUser, physicianName, inviteUrl),
      emailType: 'admin_invite' // Required for backend API validation
    };

    try {
      logger.debug('📧 Sending admin invitation email to:', adminUser.email);

      // Send via backend API (Vercel serverless function)
      const result = await this.sendEmail(emailData);

      if (result.success) {
        logger.debug('✅ Admin invitation email sent successfully');
        return { success: true, messageId: result.messageId };
      } else {
        logger.error('Failed to send admin invitation email', { error: result.error });
        return { success: false, error: result.error };
      }
    } catch (error) {
      logger.error('Email service error', { error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send external provider invitation email
   * @param {Object} invitation - Invitation object (can be from ExternalProviderInvitation.createInvitation() or simple data)
   * @returns {Object} Result with success status
   */
  async sendExternalProviderInvite(invitation) {
    // Handle both camelCase and snake_case field names (database returns snake_case)
    const providerEmail = invitation.provider_email || invitation.providerEmail;
    const providerName = invitation.provider_name || invitation.providerName || 'Healthcare Provider';
    const salesRepName = invitation.sales_rep_name || invitation.salesRepName || 'Your RepConnect Contact';
    const salesRepEmail = invitation.sales_rep_email || invitation.salesRepEmail || '';
    const salesRepCompany = invitation.sales_rep_company || invitation.salesRepCompany || '';

    if (!invitation || !providerEmail) {
      logger.error('Invalid invitation object or missing provider email');
      return { success: false, error: 'Invalid invitation data' };
    }

    let emailContent = invitation.emailContent;

    // Generate email content if not provided (simplified invite flow)
    if (!emailContent) {
      emailContent = this.generateSimpleInviteEmail({
        providerName,
        providerEmail,
        salesRepName,
        salesRepEmail,
        salesRepCompany
      });
    }

    const emailData = {
      to: providerEmail,
      subject: emailContent.subject,
      html: emailContent.htmlBody,
      text: emailContent.textBody,
      emailType: 'external_provider_invite'
    };

    try {
      logger.debug('📧 Sending external provider invitation to:', providerEmail);

      // Send via backend API (Vercel serverless function)
      const result = await this.sendEmail(emailData);

      if (result.success) {
        logger.debug('✅ External provider invitation sent successfully', {
          to: providerEmail,
          messageId: result.messageId
        });
        return { success: true, messageId: result.messageId };
      } else {
        logger.error('Failed to send external provider invitation', { error: result.error });
        return { success: false, error: result.error };
      }
    } catch (error) {
      logger.error('Email service error sending external provider invite', { error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate simple invitation email for the simplified external provider flow
   * @param {Object} data - Email data
   * @returns {Object} Email content with subject, htmlBody, textBody
   */
  generateSimpleInviteEmail({ providerName, salesRepName, salesRepEmail, salesRepCompany }) {
    const signupUrl = 'https://login.meetwithreps.com';

    const subject = `${salesRepName} has invited you to join RepConnect`;

    const htmlBody = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f3f4f6;">
  <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f3f4f6;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px;">
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; padding: 40px; text-align: center; border-radius: 12px 12px 0 0;">
              <img src="https://login.meetwithreps.com/logo192.png" alt="RepConnect" width="80" height="80" style="display: block; margin: 0 auto 16px auto; border-radius: 12px;">
              <h1 style="margin: 0 0 8px 0; font-size: 28px; font-weight: 700;">You're Invited to RepConnect!</h1>
              <p style="margin: 0; opacity: 0.9; font-size: 16px;">Simplify your sales representative lunch meetings</p>
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="background: #ffffff; padding: 40px; border-left: 1px solid #e5e7eb; border-right: 1px solid #e5e7eb;">
              <p style="font-size: 16px; color: #374151; margin: 0 0 20px 0;">
                Hi ${providerName},
              </p>
              <p style="font-size: 16px; color: #374151; margin: 0 0 20px 0;">
                <strong>${salesRepName}</strong>${salesRepCompany ? ` from ${salesRepCompany}` : ''} has invited you to join RepConnect - the easiest way to manage sales representative lunch meetings at your practice.
              </p>

              <div style="background: #f8fafc; border-radius: 8px; padding: 20px; margin: 24px 0;">
                <h3 style="margin: 0 0 12px 0; color: #1e293b; font-size: 16px;">With RepConnect, you can:</h3>
                <ul style="margin: 0; padding: 0 0 0 20px; color: #475569;">
                  <li style="margin-bottom: 8px;">View your upcoming calendar</li>
                  <li style="margin-bottom: 8px;">Set your availability preferences</li>
                  <li style="margin-bottom: 8px;">Review and approve meeting requests</li>
                  <li style="margin-bottom: 8px;">Manage your dining preferences</li>
                  <li style="margin-bottom: 8px;">Coordinate with your office staff</li>
                  <li style="margin-bottom: 8px;">Control your schedule</li>
                </ul>
              </div>

              <div style="text-align: center; margin: 32px 0;">
                <a href="${signupUrl}" style="display: inline-block; background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; text-decoration: none; padding: 16px 32px; border-radius: 8px; font-weight: 600; font-size: 16px;">
                  Join RepConnect - It's Free!
                </a>
              </div>

              <p style="font-size: 14px; color: #6b7280; margin: 24px 0 0 0; text-align: center;">
                RepConnect is completely free for healthcare providers.
              </p>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background: #f9fafb; padding: 24px 40px; text-align: center; border-radius: 0 0 12px 12px; border: 1px solid #e5e7eb; border-top: none;">
              <p style="font-size: 14px; color: #6b7280; margin: 0 0 8px 0;">
                Questions? Reply to this email or contact ${salesRepEmail}
              </p>
              <p style="font-size: 12px; color: #9ca3af; margin: 0;">
                © ${new Date().getFullYear()} RepConnect, LLC | <a href="https://meetwithreps.com" style="color: #6b7280;">meetwithreps.com</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

    const textBody = `
You're Invited to RepConnect!

Hi ${providerName},

${salesRepName}${salesRepCompany ? ` from ${salesRepCompany}` : ''} has invited you to join RepConnect - the easiest way to manage sales representative lunch meetings at your practice.

With RepConnect, you can:
- View your upcoming calendar
- Set your availability preferences
- Review and approve meeting requests
- Manage your dining preferences
- Coordinate with your office staff
- Control your schedule

Join RepConnect (It's Free!): ${signupUrl}

RepConnect is completely free for healthcare providers.

Questions? Reply to this email or contact ${salesRepEmail}

© ${new Date().getFullYear()} RepConnect, LLC | meetwithreps.com
`;

    return {
      subject,
      htmlBody,
      textBody
    };
  }

  /**
   * Send email via backend API (Vercel serverless function)
   * This avoids CORS issues and keeps API keys secure on the server
   */
  async sendEmail(emailData) {
    try {
      logger.debug('📧 Sending email via backend API:', {
        to: emailData.to,
        subject: emailData.subject
      });

      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: emailData.to,
          subject: emailData.subject,
          html: emailData.html,
          text: emailData.text,
          emailType: emailData.emailType || 'admin_invite' // Default to admin_invite
        }),
      });

      const result = await response.json();

      logger.debug('📨 Email API Response:', {
        status: response.status,
        ok: response.ok,
        result: result
      });

      if (response.ok && result.success) {
        logger.debug('✅ Email sent successfully', { messageId: result.messageId });
        return { success: true, messageId: result.messageId };
      } else {
        logger.error('❌ Email API Error', {
          status: response.status,
          result: result
        });
        return { success: false, error: result.error || 'Failed to send email' };
      }

    } catch (error) {
      logger.error('❌ Email sending exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email via Resend (recommended)
   */
  async sendViaResend(emailData) {
    try {
      const payload = {
        from: this.fromEmail,
        to: emailData.to,
        subject: emailData.subject,
        html: emailData.html,
        text: emailData.text
      };

      logger.debug('📧 Sending email via Resend:', {
        from: this.fromEmail,
        to: emailData.to,
        subject: emailData.subject
      });

      const response = await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      logger.debug('📨 Resend API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        result: result
      });

      if (response.ok) {
        logger.debug('✅ Email sent successfully via Resend', { messageId: result.id });
        return { success: true, messageId: result.id };
      } else {
        logger.error('❌ Resend API Error', {
          status: response.status,
          statusText: response.statusText,
          result: result,
          message: result.message,
          error: result.error
        });
        return { success: false, error: result.message || result.error || `HTTP ${response.status}: ${response.statusText}` };
      }
    } catch (error) {
      logger.error('Resend API Exception', { error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email via SendGrid
   */
  async sendViaSendGrid(emailData) {
    try {
      const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          personalizations: [{
            to: [{ email: emailData.to }],
            subject: emailData.subject
          }],
          from: { email: this.fromEmail },
          content: [
            { type: 'text/plain', value: emailData.text },
            { type: 'text/html', value: emailData.html }
          ]
        }),
      });

      if (response.ok) {
        return { success: true, messageId: response.headers.get('x-message-id') };
      } else {
        const error = await response.text();
        return { success: false, error: error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Send email via Mailgun
   */
  async sendViaMailgun(emailData) {
    const domain = process.env.REACT_APP_MAILGUN_DOMAIN;
    
    if (!domain) {
      return { success: false, error: 'Mailgun domain not configured' };
    }

    try {
      const formData = new FormData();
      formData.append('from', this.fromEmail);
      formData.append('to', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('text', emailData.text);
      formData.append('html', emailData.html);

      const response = await fetch(`https://api.mailgun.net/v3/${domain}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`api:${this.apiKey}`)}`,
        },
        body: formData,
      });

      const result = await response.json();
      
      if (response.ok) {
        return { success: true, messageId: result.id };
      } else {
        return { success: false, error: result.message || 'Unknown error' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate HTML template for admin invitation
   */
  generateAdminInviteHTML(adminUser, physicianName, inviteUrl) {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Admin Invitation - RepConnect</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #2563eb; color: white; padding: 20px; text-align: center; }
          .content { padding: 20px; background: #f9fafb; }
          .button { 
            display: inline-block; 
            background: #2563eb; 
            color: white; 
            padding: 12px 24px; 
            text-decoration: none; 
            border-radius: 6px; 
            margin: 20px 0;
          }
          .footer { padding: 20px; text-align: center; color: #666; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>RepConnect</h1>
            <h2>Admin Invitation</h2>
          </div>
          
          <div class="content">
            <h3>Hi ${adminUser.name || adminUser.email},</h3>
            
            <p>You've been invited to manage <strong>${physicianName}'s</strong> RepConnect portal as an administrator.</p>
            
            <p>As an admin, you'll be able to:</p>
            <ul>
              <li>Manage meeting requests on behalf of ${physicianName}</li>
              <li>Schedule and coordinate appointments</li>
              <li>Access compliance reports and analytics</li>
              <li>Manage dining preferences and availability</li>
            </ul>
            
            <p>Click the button below to accept your invitation and set up your admin account:</p>
            
            <p style="text-align: center;">
              <a href="${inviteUrl}" class="button">Accept Invitation</a>
            </p>
            
            <p><small>If the button doesn't work, copy and paste this link into your browser:</small></p>
            <p><small>${inviteUrl}</small></p>
            
            <p><strong>Note:</strong> This invitation will expire in 7 days for security purposes.</p>
            
            <p>If you have any questions, please contact your RepConnect administrator.</p>
          </div>
          
          <div class="footer">
            <p>&copy; 2025 RepConnect. All rights reserved.</p>
            <p>This is an automated email. Please do not reply to this message.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Generate plain text template for admin invitation
   */
  generateAdminInviteText(adminUser, physicianName, inviteUrl) {
    return `
RepConnect - Admin Invitation

Hi ${adminUser.name || adminUser.email},

You've been invited to manage ${physicianName}'s RepConnect portal as an administrator.

As an admin, you'll be able to:
- Manage meeting requests on behalf of ${physicianName}
- Schedule and coordinate appointments
- Access compliance reports and analytics
- Manage dining preferences and availability

To accept your invitation and set up your admin account, visit:
${inviteUrl}

Note: This invitation will expire in 7 days for security purposes.

If you have any questions, please contact your RepConnect administrator.

---
© ${new Date().getFullYear()} RepConnect, LLC and meetwithreps.com
This is an automated email. Please do not reply to this message.
    `;
  }

  /**
   * Send meeting booking notification to physician
   * @param {Object} meetingDetails - Meeting details
   * @param {string} meetingDetails.physicianName - Name of physician
   * @param {string} meetingDetails.physicianEmail - Email of physician
   * @param {string} meetingDetails.salesRepName - Name of sales rep who booked
   * @param {string} meetingDetails.meetingDate - Meeting date (YYYY-MM-DD)
   * @param {string} meetingDetails.meetingTime - Meeting time (HH:MM AM/PM)
   * @param {string} meetingDetails.locationName - Practice location name
   * @param {string} meetingDetails.locationAddress - Practice location address
   * @param {string} meetingDetails.restaurantName - Restaurant name
   * @param {string} meetingDetails.notes - Optional meeting notes
   * @param {number} meetingDetails.totalAttendees - Total number of attendees
   * @returns {Object} Result with success status
   */
  async sendMeetingBookingNotification(meetingDetails) {
    const {
      physicianName,
      physicianEmail,
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      locationAddress,
      restaurantName,
      notes,
      totalAttendees
    } = meetingDetails;

    if (!physicianEmail) {
      logger.error('Cannot send meeting notification: missing physician email');
      return { success: false, error: 'Missing physician email' };
    }

    const emailData = {
      to: physicianEmail,
      subject: `New Meeting Booked - ${meetingDate} at ${meetingTime}`,
      html: this.generateMeetingNotificationHTML(meetingDetails),
      text: this.generateMeetingNotificationText(meetingDetails),
      emailType: 'meeting_notification'
    };

    try {
      logger.debug('📧 Sending meeting booking notification to:', physicianEmail);

      const result = await this.sendEmail(emailData);

      if (result.success) {
        logger.debug('✅ Meeting notification sent successfully', {
          to: physicianEmail,
          messageId: result.messageId
        });
        return { success: true, messageId: result.messageId };
      } else {
        logger.error('Failed to send meeting notification', { error: result.error });
        return { success: false, error: result.error };
      }
    } catch (error) {
      logger.error('Email service error sending meeting notification', { error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate HTML template for meeting booking notification
   */
  generateMeetingNotificationHTML(meetingDetails) {
    const {
      physicianName,
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      locationAddress,
      restaurantName,
      notes,
      totalAttendees
    } = meetingDetails;

    // Format the date nicely
    const formattedDate = new Date(meetingDate + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Meeting Booked - RepConnect</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #2563eb; color: white; padding: 20px; text-align: center; }
          .content { padding: 20px; background: #f9fafb; }
          .detail-box {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 16px;
            margin: 16px 0;
          }
          .detail-row {
            display: flex;
            padding: 8px 0;
            border-bottom: 1px solid #f3f4f6;
          }
          .detail-row:last-child { border-bottom: none; }
          .detail-label {
            font-weight: bold;
            color: #6b7280;
            min-width: 140px;
          }
          .detail-value { color: #111827; }
          .button {
            display: inline-block;
            background: #2563eb;
            color: white;
            padding: 12px 24px;
            text-decoration: none;
            border-radius: 6px;
            margin: 20px 0;
          }
          .footer { padding: 20px; text-align: center; color: #666; font-size: 12px; }
          .notes-box {
            background: #fef3c7;
            border: 1px solid #fbbf24;
            border-radius: 6px;
            padding: 12px;
            margin: 16px 0;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>RepConnect</h1>
            <h2>New Meeting Scheduled</h2>
          </div>

          <div class="content">
            <h3>Hi ${physicianName},</h3>

            <p>A new lunch meeting has been scheduled with <strong>${salesRepName}</strong>.</p>

            <div class="detail-box">
              <div class="detail-row">
                <span class="detail-label">Date & Time:</span>
                <span class="detail-value">${formattedDate} at ${meetingTime}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Location:</span>
                <span class="detail-value">
                  ${locationName}${locationAddress ? `<br><small>${locationAddress}</small>` : ''}
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Restaurant:</span>
                <span class="detail-value">${restaurantName}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Total Attendees:</span>
                <span class="detail-value">${totalAttendees} ${totalAttendees === 1 ? 'person' : 'people'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Sales Rep:</span>
                <span class="detail-value">${salesRepName}</span>
              </div>
            </div>

            ${notes ? `
            <div class="notes-box">
              <strong>Meeting Notes:</strong><br>
              ${notes}
            </div>
            ` : ''}

            <p style="text-align: center;">
              <a href="${typeof window !== 'undefined' ? window.location.origin : 'https://login.meetwithreps.com'}/dashboard" class="button">View in Dashboard</a>
            </p>

            <p>You can view all meeting details and manage your schedule by logging into your RepConnect dashboard.</p>

            <p>If you have any questions or need to make changes, please contact ${salesRepName}.</p>
          </div>

          <div class="footer">
            <p>&copy; ${new Date().getFullYear()} RepConnect. All rights reserved.</p>
            <p>This is an automated notification. To manage your meeting preferences, log in to your dashboard.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Generate plain text template for meeting booking notification
   */
  generateMeetingNotificationText(meetingDetails) {
    const {
      physicianName,
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      locationAddress,
      restaurantName,
      notes,
      totalAttendees
    } = meetingDetails;

    // Format the date nicely
    const formattedDate = new Date(meetingDate + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
RepConnect - New Meeting Scheduled

Hi ${physicianName},

A new lunch meeting has been scheduled with ${salesRepName}.

MEETING DETAILS:
----------------
Date & Time: ${formattedDate} at ${meetingTime}
Location: ${locationName}${locationAddress ? '\n          ' + locationAddress : ''}
Restaurant: ${restaurantName}
Total Attendees: ${totalAttendees} ${totalAttendees === 1 ? 'person' : 'people'}
Sales Rep: ${salesRepName}

${notes ? `MEETING NOTES:\n${notes}\n\n` : ''}
You can view all meeting details and manage your schedule by logging into your RepConnect dashboard at:
${typeof window !== 'undefined' ? window.location.origin : 'https://login.meetwithreps.com'}/dashboard

If you have any questions or need to make changes, please contact ${salesRepName}.

---
© ${new Date().getFullYear()} RepConnect, LLC and meetwithreps.com
This is an automated notification. To manage your meeting preferences, log in to your dashboard.
    `;
  }

  /**
   * Send multi-physician meeting invitation to invited physicians
   * @param {Object} invitationDetails - Invitation details
   * @param {string} invitationDetails.physicianName - Name of invited physician
   * @param {string} invitationDetails.physicianEmail - Invited physician's email
   * @param {string} invitationDetails.organizerName - Name of organizer physician
   * @param {string} invitationDetails.salesRepName - Name of sales rep
   * @param {string} invitationDetails.meetingDate - Meeting date (YYYY-MM-DD)
   * @param {string} invitationDetails.meetingTime - Meeting time (HH:MM AM/PM)
   * @param {string} invitationDetails.locationName - Practice location name
   * @param {string} invitationDetails.locationAddress - Practice location address
   * @param {string} invitationDetails.restaurantName - Restaurant name
   * @param {number} invitationDetails.totalAttendees - Total number of attendees
   * @param {string} invitationDetails.meetingId - Meeting ID for RSVP link
   * @returns {Object} Result with success status
   */
  async sendPhysicianInvitation(invitationDetails) {
    const {
      physicianName,
      physicianEmail,
      organizerName,
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      locationAddress,
      restaurantName,
      totalAttendees,
      meetingId
    } = invitationDetails;

    if (!physicianEmail) {
      logger.error('Cannot send physician invitation: missing physician email');
      return { success: false, error: 'Missing physician email' };
    }

    // Generate RSVP link (points to login page, user can RSVP after logging in)
    const rsvpUrl = `${process.env.REACT_APP_URL || 'https://login.meetwithreps.com'}/login`;

    const emailData = {
      to: physicianEmail,
      subject: `Meeting Invitation - ${meetingDate} at ${meetingTime}`,
      html: this.generatePhysicianInvitationHTML({ ...invitationDetails, rsvpUrl }),
      text: this.generatePhysicianInvitationText({ ...invitationDetails, rsvpUrl }),
      emailType: 'physician_invitation'
    };

    try {
      logger.debug('📧 Sending physician invitation to:', physicianEmail);

      const result = await this.sendEmail(emailData);

      if (result.success) {
        logger.debug('✅ Physician invitation sent successfully', {
          to: physicianEmail,
          messageId: result.messageId
        });
        return { success: true, messageId: result.messageId };
      } else {
        logger.error('Failed to send physician invitation', { error: result.error });
        return { success: false, error: result.error };
      }
    } catch (error) {
      logger.error('Email service error sending physician invitation', { error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate HTML template for physician invitation
   */
  generatePhysicianInvitationHTML(invitationDetails) {
    const {
      physicianName,
      organizerName,
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      locationAddress,
      restaurantName,
      totalAttendees,
      rsvpUrl
    } = invitationDetails;

    // Format the date nicely
    const formattedDate = new Date(meetingDate + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Meeting Invitation - RepConnect</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #2563eb; color: white; padding: 20px; text-align: center; }
          .content { padding: 20px; background: #f9fafb; }
          .detail-box {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 16px;
            margin: 16px 0;
          }
          .detail-row {
            display: flex;
            padding: 8px 0;
            border-bottom: 1px solid #f3f4f6;
          }
          .detail-row:last-child { border-bottom: none; }
          .detail-label {
            font-weight: bold;
            color: #6b7280;
            min-width: 140px;
          }
          .detail-value { flex: 1; }
          .button {
            display: inline-block;
            padding: 12px 24px;
            margin: 8px 8px 8px 0;
            background: #2563eb;
            color: white;
            text-decoration: none;
            border-radius: 6px;
            font-weight: bold;
          }
          .button-accept { background: #16a34a; }
          .footer {
            text-align: center;
            padding: 20px;
            color: #6b7280;
            font-size: 14px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1 style="margin: 0;">Meeting Invitation</h1>
            <p style="margin: 8px 0 0 0; opacity: 0.9;">You've been invited to a lunch meeting</p>
          </div>

          <div class="content">
            <p>Hi ${physicianName},</p>

            <p><strong>${organizerName}</strong> has invited you to join a lunch meeting organized by ${salesRepName}.</p>

            <div class="detail-box">
              <div class="detail-row">
                <div class="detail-label">📅 Date:</div>
                <div class="detail-value">${formattedDate}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">⏰ Time:</div>
                <div class="detail-value">${meetingTime}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">📍 Location:</div>
                <div class="detail-value">${locationName}${locationAddress ? '<br>' + locationAddress : ''}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">🍽️ Restaurant:</div>
                <div class="detail-value">${restaurantName}</div>
              </div>
              <div class="detail-row">
                <div class="detail-label">👥 Attendees:</div>
                <div class="detail-value">${totalAttendees} total (physicians + staff + sales rep)</div>
              </div>
            </div>

            <p><strong>Please log in to RepConnect to accept or decline this invitation:</strong></p>

            <div style="text-align: center; margin: 24px 0;">
              <a href="${rsvpUrl}" class="button button-accept">Log In to Respond</a>
            </div>

            <p style="color: #6b7280; font-size: 14px; margin-top: 24px;">
              After logging in, you'll find this meeting in your "Upcoming Meetings" section where you can accept or decline the invitation.
            </p>
          </div>

          <div class="footer">
            <p><strong>RepConnect</strong> - Streamlining Healthcare Professional Meetings</p>
            <p style="margin-top: 8px;">This is an automated message. Please do not reply to this email.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Generate plain text version for physician invitation
   */
  generatePhysicianInvitationText(invitationDetails) {
    const {
      physicianName,
      organizerName,
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      locationAddress,
      restaurantName,
      totalAttendees,
      rsvpUrl
    } = invitationDetails;

    const formattedDate = new Date(meetingDate + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
MEETING INVITATION

Hi ${physicianName},

${organizerName} has invited you to join a lunch meeting organized by ${salesRepName}.

MEETING DETAILS:
Date: ${formattedDate}
Time: ${meetingTime}
Location: ${locationName}${locationAddress ? '\n' + locationAddress : ''}
Restaurant: ${restaurantName}
Total Attendees: ${totalAttendees} (physicians + staff + sales rep)

RESPOND TO INVITATION:
Please log in to RepConnect to accept or decline this invitation:
${rsvpUrl}

After logging in, you'll find this meeting in your "Upcoming Meetings" section where you can accept or decline the invitation.

---
RepConnect - Streamlining Healthcare Professional Meetings
This is an automated message. Please do not reply to this email.
    `.trim();
  }

  /**
   * Send meeting cancellation notification to physician and admins
   * @param {Object} cancellationDetails - Cancellation details
   * @param {string} cancellationDetails.physicianName - Name of physician
   * @param {string} cancellationDetails.physicianEmail - Email of physician
   * @param {Array} cancellationDetails.adminEmails - Array of admin emails to notify
   * @param {string} cancellationDetails.salesRepName - Name of sales rep who cancelled
   * @param {string} cancellationDetails.meetingDate - Meeting date (YYYY-MM-DD)
   * @param {string} cancellationDetails.meetingTime - Meeting time (HH:MM AM/PM)
   * @param {string} cancellationDetails.locationName - Practice location name
   * @param {string} cancellationDetails.restaurantName - Restaurant name
   * @param {string} cancellationDetails.cancellationReason - Reason for cancellation
   * @param {string} cancellationDetails.cancellationCategory - Category (scheduling_conflict, emergency, etc.)
   * @param {boolean} cancellationDetails.allowReschedule - Whether rescheduling is allowed
   * @param {Array} cancellationDetails.suggestedDates - Optional suggested alternative dates
   * @returns {Object} Result with success status and list of sent/failed emails
   */
  async sendMeetingCancellationNotification(cancellationDetails) {
    const {
      physicianName,
      physicianEmail,
      adminEmails = [],
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      restaurantName,
      cancellationReason,
      cancellationCategory,
      allowReschedule,
      suggestedDates = []
    } = cancellationDetails;

    const results = {
      sent: [],
      failed: []
    };

    // Collect all recipient emails (physician + admins)
    const recipients = [];
    if (physicianEmail) recipients.push({ email: physicianEmail, role: 'physician', name: physicianName });
    adminEmails.forEach(adminEmail => {
      if (adminEmail) recipients.push({ email: adminEmail, role: 'admin', name: 'Admin' });
    });

    if (recipients.length === 0) {
      logger.error('Cannot send cancellation notification: no recipients');
      return { success: false, error: 'No recipients provided', results };
    }

    // Send email to each recipient
    for (const recipient of recipients) {
      const emailData = {
        to: recipient.email,
        subject: `Meeting Cancelled - ${meetingDate} at ${meetingTime}`,
        html: this.generateCancellationNotificationHTML({
          ...cancellationDetails,
          recipientName: recipient.name,
          recipientRole: recipient.role
        }),
        text: this.generateCancellationNotificationText({
          ...cancellationDetails,
          recipientName: recipient.name,
          recipientRole: recipient.role
        }),
        emailType: 'meeting_cancellation'
      };

      try {
        logger.debug(`📧 Sending cancellation notification to ${recipient.role}:`, recipient.email);

        const result = await this.sendEmail(emailData);

        if (result.success) {
          logger.debug(`✅ Cancellation notification sent to ${recipient.role}`, {
            to: recipient.email,
            messageId: result.messageId
          });
          results.sent.push(recipient.email);
        } else {
          logger.error(`Failed to send cancellation notification to ${recipient.role}`, {
            email: recipient.email,
            error: result.error
          });
          results.failed.push({ email: recipient.email, error: result.error });
        }
      } catch (error) {
        logger.error(`Email service error sending cancellation to ${recipient.role}`, {
          email: recipient.email,
          error: error.message
        });
        results.failed.push({ email: recipient.email, error: error.message });
      }
    }

    const allSent = results.failed.length === 0;
    return {
      success: allSent,
      results,
      message: allSent
        ? 'All notifications sent successfully'
        : `Sent to ${results.sent.length}, failed to send to ${results.failed.length}`
    };
  }

  /**
   * Generate HTML template for meeting cancellation notification
   */
  generateCancellationNotificationHTML(cancellationDetails) {
    const {
      recipientName,
      recipientRole,
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      restaurantName,
      cancellationReason,
      cancellationCategory,
      allowReschedule,
      suggestedDates = []
    } = cancellationDetails;

    // Format the date nicely
    const formattedDate = new Date(meetingDate + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Category labels
    const categoryLabels = {
      scheduling_conflict: 'Scheduling Conflict',
      emergency: 'Emergency',
      illness: 'Illness',
      travel: 'Travel Issues',
      weather: 'Weather/External Factors',
      personal: 'Personal Reasons',
      other: 'Other'
    };

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Meeting Cancelled - RepConnect</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #dc2626; color: white; padding: 20px; text-align: center; }
          .content { padding: 20px; background: #f9fafb; }
          .alert-box {
            background: #fef2f2;
            border: 2px solid #dc2626;
            border-radius: 8px;
            padding: 16px;
            margin: 16px 0;
          }
          .detail-box {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 16px;
            margin: 16px 0;
          }
          .detail-row {
            display: flex;
            padding: 8px 0;
            border-bottom: 1px solid #f3f4f6;
          }
          .detail-row:last-child { border-bottom: none; }
          .detail-label {
            font-weight: bold;
            color: #6b7280;
            min-width: 140px;
          }
          .detail-value { color: #111827; }
          .reason-box {
            background: #fffbeb;
            border: 1px solid #fbbf24;
            border-radius: 6px;
            padding: 12px;
            margin: 16px 0;
          }
          .button {
            display: inline-block;
            background: #2563eb;
            color: white;
            padding: 12px 24px;
            text-decoration: none;
            border-radius: 6px;
            margin: 20px 0;
          }
          .footer { padding: 20px; text-align: center; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>RepConnect</h1>
            <h2>⚠️ Meeting Cancelled</h2>
          </div>

          <div class="content">
            <h3>Hi ${recipientName},</h3>

            <div class="alert-box">
              <p style="margin: 0; font-size: 16px;"><strong>A scheduled meeting has been cancelled.</strong></p>
            </div>

            <p><strong>${salesRepName}</strong> has cancelled the following meeting:</p>

            <div class="detail-box">
              <div class="detail-row">
                <span class="detail-label">Date & Time:</span>
                <span class="detail-value">${formattedDate} at ${meetingTime}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Location:</span>
                <span class="detail-value">${locationName}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Restaurant:</span>
                <span class="detail-value">${restaurantName}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Cancelled By:</span>
                <span class="detail-value">${salesRepName}</span>
              </div>
            </div>

            ${cancellationReason ? `
            <div class="reason-box">
              <strong>Reason for Cancellation:</strong><br>
              ${categoryLabels[cancellationCategory] || 'Other'}<br><br>
              ${cancellationReason}
            </div>
            ` : ''}

            ${allowReschedule && suggestedDates.length > 0 ? `
            <div class="detail-box">
              <strong>Suggested Alternative Dates:</strong>
              <ul style="margin: 8px 0; padding-left: 20px;">
                ${suggestedDates.map(date => {
                  const formatted = new Date(date).toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  });
                  return `<li>${formatted}</li>`;
                }).join('')}
              </ul>
            </div>
            ` : ''}

            ${allowReschedule && suggestedDates.length === 0 ? `
            <p>${salesRepName} is open to rescheduling this meeting. You can contact them directly to find a new time.</p>
            ` : ''}

            <p style="text-align: center;">
              <a href="${typeof window !== 'undefined' ? window.location.origin : 'https://login.meetwithreps.com'}/dashboard" class="button">View Dashboard</a>
            </p>

            <p>This meeting has been removed from your calendar. You can view your updated schedule in your RepConnect dashboard.</p>

            <p>If you have any questions, please contact ${salesRepName}.</p>
          </div>

          <div class="footer">
            <p>&copy; ${new Date().getFullYear()} RepConnect. All rights reserved.</p>
            <p>This is an automated notification. To manage your meeting preferences, log in to your dashboard.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Generate plain text template for meeting cancellation notification
   */
  generateCancellationNotificationText(cancellationDetails) {
    const {
      recipientName,
      salesRepName,
      meetingDate,
      meetingTime,
      locationName,
      restaurantName,
      cancellationReason,
      cancellationCategory,
      allowReschedule,
      suggestedDates = []
    } = cancellationDetails;

    // Format the date nicely
    const formattedDate = new Date(meetingDate + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Category labels
    const categoryLabels = {
      scheduling_conflict: 'Scheduling Conflict',
      emergency: 'Emergency',
      illness: 'Illness',
      travel: 'Travel Issues',
      weather: 'Weather/External Factors',
      personal: 'Personal Reasons',
      other: 'Other'
    };

    let text = `
RepConnect - Meeting Cancelled

Hi ${recipientName},

⚠️ A scheduled meeting has been cancelled.

${salesRepName} has cancelled the following meeting:

MEETING DETAILS:
----------------
Date & Time: ${formattedDate} at ${meetingTime}
Location: ${locationName}
Restaurant: ${restaurantName}
Cancelled By: ${salesRepName}
`;

    if (cancellationReason) {
      text += `
REASON FOR CANCELLATION:
${categoryLabels[cancellationCategory] || 'Other'}

${cancellationReason}
`;
    }

    if (allowReschedule && suggestedDates.length > 0) {
      text += `
SUGGESTED ALTERNATIVE DATES:
`;
      suggestedDates.forEach(date => {
        const formatted = new Date(date).toLocaleString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
        text += `  - ${formatted}\n`;
      });
    } else if (allowReschedule) {
      text += `
${salesRepName} is open to rescheduling this meeting. You can contact them directly to find a new time.
`;
    }

    text += `
This meeting has been removed from your calendar. You can view your updated schedule in your RepConnect dashboard at:
${typeof window !== 'undefined' ? window.location.origin : 'https://login.meetwithreps.com'}/dashboard

If you have any questions, please contact ${salesRepName}.

---
© ${new Date().getFullYear()} RepConnect, LLC and meetwithreps.com
This is an automated notification. To manage your meeting preferences, log in to your dashboard.
    `;

    return text;
  }

  /**
   * Send welcome email after admin account is created
   */
  async sendAdminWelcome(adminUser, physicianName) {
    const emailData = {
      to: adminUser.email,
      subject: `Welcome to RepConnect - ${physicianName}'s Portal`,
      html: this.generateWelcomeHTML(adminUser, physicianName),
      text: this.generateWelcomeText(adminUser, physicianName)
    };

    return await this.sendEmail(emailData);
  }

  generateWelcomeHTML(adminUser, physicianName) {
    return `
      <!DOCTYPE html>
      <html>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: #2563eb; color: white; padding: 20px; text-align: center;">
            <h1>Welcome to RepConnect!</h1>
          </div>
          
          <div style="padding: 20px; background: #f9fafb;">
            <h3>Hi ${adminUser.name || adminUser.email},</h3>
            
            <p>Welcome to RepConnect! Your admin account for <strong>${physicianName}'s</strong> portal has been successfully created.</p>
            
            <p>You can now log in and start managing the portal. Your login credentials are:</p>
            <ul>
              <li><strong>Email:</strong> ${adminUser.email}</li>
              <li><strong>Password:</strong> Use the password you created during setup</li>
            </ul>
            
            <p>Visit <a href="${window.location.origin}/admin-login">RepConnect Admin Login</a> to get started.</p>
            
            <p>Thank you for joining RepConnect!</p>
          </div>
          
          <div style="padding: 20px; text-align: center; color: #666;">
            <p>&copy; 2025 RepConnect. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  generateWelcomeText(adminUser, physicianName) {
    return `
Welcome to RepConnect!

Hi ${adminUser.name || adminUser.email},

Welcome to RepConnect! Your admin account for ${physicianName}'s portal has been successfully created.

You can now log in and start managing the portal. Your login credentials are:
- Email: ${adminUser.email}
- Password: Use the password you created during setup

Visit ${window.location.origin}/admin-login to get started.

Thank you for joining RepConnect!

---
© ${new Date().getFullYear()} RepConnect, LLC and meetwithreps.com
    `;
  }

  /**
   * Send welcome email to new users after onboarding
   * Content is customized based on user type
   * @param {Object} userDetails - User details
   * @param {string} userDetails.email - User's email
   * @param {string} userDetails.firstName - User's first name
   * @param {string} userDetails.lastName - User's last name
   * @param {string} userDetails.userType - 'sales', 'customer', or 'office_staff'
   * @returns {Object} Result with success status
   */
  async sendWelcomeEmail(userDetails) {
    const { email, firstName, lastName, userType } = userDetails;

    if (!email) {
      logger.error('Cannot send welcome email: missing email');
      return { success: false, error: 'Missing email' };
    }

    const userName = firstName || 'there';

    const emailData = {
      to: email,
      subject: `Welcome to RepConnect, ${userName}! 🎉`,
      html: this.generateWelcomeEmailHTML(userDetails),
      text: this.generateWelcomeEmailText(userDetails),
      emailType: 'welcome'
    };

    try {
      logger.debug('📧 Sending welcome email to:', email);

      const result = await this.sendEmail(emailData);

      if (result.success) {
        logger.debug('✅ Welcome email sent successfully', {
          to: email,
          userType: userType,
          messageId: result.messageId
        });
        return { success: true, messageId: result.messageId };
      } else {
        logger.error('Failed to send welcome email', { error: result.error });
        return { success: false, error: result.error };
      }
    } catch (error) {
      logger.error('Email service error sending welcome email', { error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Get tips based on user type
   */
  getWelcomeTips(userType) {
    if (userType === 'sales') {
      return {
        title: 'Tips to Get Started as a Sales Representative',
        tips: [
          {
            icon: '🔍',
            title: 'Find Healthcare Providers',
            description: 'Use our powerful search to discover physicians in your territory. Filter by specialty, location, and availability.'
          },
          {
            icon: '📅',
            title: 'Book Your First Meeting',
            description: 'Schedule lunch meetings with just a few clicks. Select a date, choose a restaurant, and send the request.'
          },
          {
            icon: '⭐',
            title: 'Build Your Favorites List',
            description: 'Save your top providers to quickly access them later. Build relationships that last.'
          },
          {
            icon: '🍽️',
            title: 'Explore Restaurant Options',
            description: 'Browse restaurants near your providers with Yelp integration. View menus, ratings, and delivery options.'
          },
          {
            icon: '📧',
            title: 'Invite Your Customers',
            description: 'The more healthcare providers you invite to RepConnect, the faster you can start booking meetings. Share the platform with your customers today!'
          }
        ]
      };
    } else {
      // customer or office_staff
      return {
        title: userType === 'office_staff'
          ? 'Tips to Get Started as an Office Manager'
          : 'Tips to Get Started as a Healthcare Provider',
        tips: [
          {
            icon: '📆',
            title: 'Set Your Availability',
            description: 'Configure your weekly schedule so sales reps know when you\'re available for meetings.'
          },
          {
            icon: '✅',
            title: 'Review Meeting Requests',
            description: 'Approve or decline meeting requests from your dashboard. You\'re always in control of your schedule.'
          },
          {
            icon: '🍽️',
            title: 'Save Your Dining Preferences',
            description: 'Add your favorite restaurants and dietary preferences. Sales reps will see these when booking.'
          },
          {
            icon: '👥',
            title: 'Manage Your Staff',
            description: 'Add office staff members who will attend lunches. Track their dietary restrictions and preferences.'
          },
          {
            icon: '📋',
            title: 'Create Saved Orders',
            description: 'Save your favorite orders at restaurants for quick reordering. Sales reps can see what you prefer.'
          }
        ]
      };
    }
  }

  /**
   * Generate HTML template for welcome email
   */
  generateWelcomeEmailHTML(userDetails) {
    const { firstName, userType } = userDetails;
    const userName = firstName || 'there';
    const tipsData = this.getWelcomeTips(userType);

    const userTypeLabel = userType === 'sales'
      ? 'Sales Representative'
      : userType === 'office_staff'
        ? 'Office Manager'
        : 'Healthcare Provider';

    const tipsHTML = tipsData.tips.map(tip => `
      <tr>
        <td style="padding: 16px; background: white; border-radius: 8px; margin-bottom: 12px;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td width="50" style="vertical-align: top; font-size: 28px;">${tip.icon}</td>
              <td style="vertical-align: top;">
                <h4 style="margin: 0 0 4px 0; color: #1f2937; font-size: 16px;">${tip.title}</h4>
                <p style="margin: 0; color: #6b7280; font-size: 14px;">${tip.description}</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
      <tr><td style="height: 12px;"></td></tr>
    `).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Welcome to RepConnect</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f3f4f6;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f3f4f6;">
          <tr>
            <td align="center" style="padding: 40px 20px;">
              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px;">

                <!-- Header -->
                <tr>
                  <td style="background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; padding: 40px; text-align: center; border-radius: 12px 12px 0 0;">
                    <img src="https://login.meetwithreps.com/logo192.png" alt="RepConnect" width="80" height="80" style="display: block; margin: 0 auto 16px auto; border-radius: 12px;">
                    <h1 style="margin: 0 0 8px 0; font-size: 28px; font-weight: 700;">Welcome to RepConnect!</h1>
                    <p style="margin: 0; opacity: 0.9; font-size: 16px;">Thank you for joining us, ${userName}!</p>
                  </td>
                </tr>

                <!-- Main Content -->
                <tr>
                  <td style="background: #ffffff; padding: 40px; border-left: 1px solid #e5e7eb; border-right: 1px solid #e5e7eb;">
                    <p style="font-size: 16px; color: #374151; margin: 0 0 20px 0;">
                      Hi ${userName},
                    </p>

                    <p style="font-size: 16px; color: #374151; margin: 0 0 20px 0;">
                      We're thrilled to have you on board as a <strong>${userTypeLabel}</strong>! RepConnect is designed to make healthcare meetings simple, efficient, and compliant.
                    </p>

                    <p style="font-size: 16px; color: #374151; margin: 0 0 30px 0;">
                      Your account is all set up and ready to go. Here are some tips to help you get the most out of RepConnect:
                    </p>

                    <!-- Tips Section -->
                    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #f9fafb; border-radius: 12px; padding: 20px;">
                      <tr>
                        <td style="padding: 20px;">
                          <h3 style="margin: 0 0 20px 0; color: #1f2937; font-size: 18px; font-weight: 600;">${tipsData.title}</h3>
                          <table cellpadding="0" cellspacing="0" border="0" width="100%">
                            ${tipsHTML}
                          </table>
                        </td>
                      </tr>
                    </table>

                    <!-- CTA Button -->
                    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-top: 30px;">
                      <tr>
                        <td align="center">
                          <a href="https://login.meetwithreps.com"
                             style="display: inline-block; background: #2563eb; color: white; padding: 14px 32px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px;">
                            Go to Your Dashboard →
                          </a>
                        </td>
                      </tr>
                    </table>

                    <p style="font-size: 16px; color: #374151; margin: 30px 0 0 0;">
                      If you have any questions, feel free to reach out to our support team. We're here to help!
                    </p>

                    <p style="font-size: 16px; color: #374151; margin: 20px 0 0 0;">
                      Best regards,<br>
                      <strong>The RepConnect Team</strong>
                    </p>
                  </td>
                </tr>

                <!-- Footer -->
                <tr>
                  <td style="background: #1f2937; color: #9ca3af; padding: 30px; text-align: center; border-radius: 0 0 12px 12px;">
                    <p style="margin: 0 0 10px 0; font-size: 14px;">
                      © ${new Date().getFullYear()} RepConnect, LLC. All rights reserved.
                    </p>
                    <p style="margin: 0; font-size: 12px;">
                      <a href="https://meetwithreps.com" style="color: #60a5fa; text-decoration: none;">meetwithreps.com</a>
                    </p>
                  </td>
                </tr>

              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
  }

  /**
   * Generate plain text template for welcome email
   */
  generateWelcomeEmailText(userDetails) {
    const { firstName, userType } = userDetails;
    const userName = firstName || 'there';
    const tipsData = this.getWelcomeTips(userType);

    const userTypeLabel = userType === 'sales'
      ? 'Sales Representative'
      : userType === 'office_staff'
        ? 'Office Manager'
        : 'Healthcare Provider';

    const tipsText = tipsData.tips.map(tip =>
      `${tip.icon} ${tip.title}\n   ${tip.description}`
    ).join('\n\n');

    return `
WELCOME TO REPCONNECT!
======================

Hi ${userName},

Thank you for joining us! 🎉

We're thrilled to have you on board as a ${userTypeLabel}! RepConnect is designed to make healthcare meetings simple, efficient, and compliant.

Your account is all set up and ready to go. Here are some tips to help you get the most out of RepConnect:

${tipsData.title.toUpperCase()}
${'-'.repeat(tipsData.title.length)}

${tipsText}

GET STARTED
-----------
Visit your dashboard: https://login.meetwithreps.com

If you have any questions, feel free to reach out to our support team. We're here to help!

Best regards,
The RepConnect Team

---
© ${new Date().getFullYear()} RepConnect, LLC. All rights reserved.
https://meetwithreps.com
    `.trim();
  }

  /**
   * Send meeting response notification to sales rep
   * Called when a customer approves or declines a meeting request
   * @param {Object} responseDetails - Response details
   * @param {string} responseDetails.salesRepEmail - Sales rep's email
   * @param {string} responseDetails.salesRepName - Sales rep's name
   * @param {string} responseDetails.customerName - Customer/physician name
   * @param {string} responseDetails.meetingDate - Meeting date (YYYY-MM-DD)
   * @param {string} responseDetails.meetingTime - Meeting time (HH:MM AM/PM)
   * @param {string} responseDetails.restaurantName - Restaurant name
   * @param {string} responseDetails.status - 'approved' or 'declined'
   * @param {string} responseDetails.responseMessage - The customer's response message
   * @returns {Object} Result with success status
   */
  async sendMeetingResponseEmail(responseDetails) {
    const {
      salesRepEmail,
      salesRepName,
      customerName,
      meetingDate,
      meetingTime,
      restaurantName,
      status,
      responseMessage
    } = responseDetails;

    if (!salesRepEmail) {
      logger.error('Cannot send meeting response email: missing sales rep email');
      return { success: false, error: 'Missing sales rep email' };
    }

    const isApproved = status === 'approved' || status === 'confirmed';
    const statusText = isApproved ? 'Approved' : 'Declined';
    const statusEmoji = isApproved ? '✅' : '❌';

    const emailData = {
      to: salesRepEmail,
      subject: `${statusEmoji} Meeting ${statusText} - ${customerName}`,
      html: this.generateMeetingResponseHTML(responseDetails),
      text: this.generateMeetingResponseText(responseDetails),
      emailType: 'meeting_notification'
    };

    try {
      logger.debug('📧 Sending meeting response email to sales rep:', salesRepEmail);

      const result = await this.sendEmail(emailData);

      if (result.success) {
        logger.debug('✅ Meeting response email sent successfully', {
          to: salesRepEmail,
          status: status,
          messageId: result.messageId
        });
        return { success: true, messageId: result.messageId };
      } else {
        logger.error('Failed to send meeting response email', { error: result.error });
        return { success: false, error: result.error };
      }
    } catch (error) {
      logger.error('Email service error sending meeting response', { error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate HTML template for meeting response notification
   */
  generateMeetingResponseHTML(responseDetails) {
    const {
      salesRepName,
      customerName,
      meetingDate,
      meetingTime,
      restaurantName,
      status,
      responseMessage
    } = responseDetails;

    const isApproved = status === 'approved' || status === 'confirmed';
    const statusText = isApproved ? 'Approved' : 'Declined';
    const headerColor = isApproved ? '#16a34a' : '#dc2626';
    const headerEmoji = isApproved ? '✅' : '❌';

    // Format the date nicely
    const formattedDate = new Date(meetingDate + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Meeting ${statusText} - RepConnect</title>
      </head>
      <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f3f4f6;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f3f4f6;">
          <tr>
            <td align="center" style="padding: 40px 20px;">
              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px;">

                <!-- Header -->
                <tr>
                  <td style="background: ${headerColor}; color: white; padding: 40px; text-align: center; border-radius: 12px 12px 0 0;">
                    <div style="font-size: 48px; margin-bottom: 16px;">${headerEmoji}</div>
                    <h1 style="margin: 0 0 8px 0; font-size: 28px; font-weight: 700;">Meeting ${statusText}</h1>
                    <p style="margin: 0; opacity: 0.9; font-size: 16px;">${customerName} has responded to your meeting request</p>
                  </td>
                </tr>

                <!-- Main Content -->
                <tr>
                  <td style="background: #ffffff; padding: 40px; border-left: 1px solid #e5e7eb; border-right: 1px solid #e5e7eb;">
                    <p style="font-size: 16px; color: #374151; margin: 0 0 20px 0;">
                      Hi ${salesRepName || 'there'},
                    </p>

                    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
                      <strong>${customerName}</strong> has <strong style="color: ${headerColor};">${statusText.toLowerCase()}</strong> your meeting request.
                    </p>

                    <!-- Meeting Details -->
                    <div style="background: #f9fafb; border-radius: 8px; padding: 20px; margin-bottom: 24px;">
                      <h3 style="margin: 0 0 16px 0; color: #1f2937; font-size: 16px; font-weight: 600;">Meeting Details</h3>
                      <table cellpadding="0" cellspacing="0" border="0" width="100%">
                        <tr>
                          <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Date:</td>
                          <td style="padding: 8px 0; color: #1f2937;">${formattedDate}</td>
                        </tr>
                        <tr>
                          <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Time:</td>
                          <td style="padding: 8px 0; color: #1f2937;">${meetingTime}</td>
                        </tr>
                        <tr>
                          <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Restaurant:</td>
                          <td style="padding: 8px 0; color: #1f2937;">${restaurantName || 'TBD'}</td>
                        </tr>
                        <tr>
                          <td style="padding: 8px 0; color: #6b7280; font-weight: 500;">Provider:</td>
                          <td style="padding: 8px 0; color: #1f2937;">${customerName}</td>
                        </tr>
                      </table>
                    </div>

                    ${responseMessage ? `
                    <!-- Response Message -->
                    <div style="background: ${isApproved ? '#f0fdf4' : '#fef2f2'}; border: 1px solid ${isApproved ? '#bbf7d0' : '#fecaca'}; border-radius: 8px; padding: 20px; margin-bottom: 24px;">
                      <h3 style="margin: 0 0 12px 0; color: #1f2937; font-size: 16px; font-weight: 600;">
                        ${isApproved ? '💬' : '📝'} Message from ${customerName}
                      </h3>
                      <p style="margin: 0; color: #374151; font-style: italic;">"${responseMessage}"</p>
                    </div>
                    ` : ''}

                    ${isApproved ? `
                    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
                      Great news! Your meeting is confirmed. Make sure to prepare accordingly and arrive on time.
                    </p>
                    ` : `
                    <p style="font-size: 16px; color: #374151; margin: 0 0 24px 0;">
                      Don't be discouraged! You can try scheduling another meeting at a different time or with a different provider.
                    </p>
                    `}

                    <!-- CTA Button -->
                    <table cellpadding="0" cellspacing="0" border="0" width="100%">
                      <tr>
                        <td align="center">
                          <a href="https://login.meetwithreps.com"
                             style="display: inline-block; background: #2563eb; color: white; padding: 14px 32px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px;">
                            View in Dashboard →
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                <!-- Footer -->
                <tr>
                  <td style="background: #1f2937; color: #9ca3af; padding: 30px; text-align: center; border-radius: 0 0 12px 12px;">
                    <p style="margin: 0 0 10px 0; font-size: 14px;">
                      © ${new Date().getFullYear()} RepConnect, LLC. All rights reserved.
                    </p>
                    <p style="margin: 0; font-size: 12px;">
                      <a href="https://meetwithreps.com" style="color: #60a5fa; text-decoration: none;">meetwithreps.com</a>
                    </p>
                  </td>
                </tr>

              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
  }

  /**
   * Generate plain text template for meeting response notification
   */
  generateMeetingResponseText(responseDetails) {
    const {
      salesRepName,
      customerName,
      meetingDate,
      meetingTime,
      restaurantName,
      status,
      responseMessage
    } = responseDetails;

    const isApproved = status === 'approved' || status === 'confirmed';
    const statusText = isApproved ? 'APPROVED' : 'DECLINED';
    const statusEmoji = isApproved ? '✅' : '❌';

    // Format the date nicely
    const formattedDate = new Date(meetingDate + 'T12:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return `
${statusEmoji} MEETING ${statusText}
${'='.repeat(30)}

Hi ${salesRepName || 'there'},

${customerName} has ${statusText.toLowerCase()} your meeting request.

MEETING DETAILS
---------------
Date: ${formattedDate}
Time: ${meetingTime}
Restaurant: ${restaurantName || 'TBD'}
Provider: ${customerName}

${responseMessage ? `MESSAGE FROM ${customerName.toUpperCase()}
${'─'.repeat(30)}
"${responseMessage}"

` : ''}${isApproved
? 'Great news! Your meeting is confirmed. Make sure to prepare accordingly and arrive on time.'
: "Don't be discouraged! You can try scheduling another meeting at a different time or with a different provider."}

View your dashboard: https://login.meetwithreps.com

---
© ${new Date().getFullYear()} RepConnect, LLC. All rights reserved.
https://meetwithreps.com
    `.trim();
  }
}

// Create singleton instance
const emailService = new EmailService();

export default emailService;